// src/components/character/characterCalculations.js
//
// Pure, client-side derivation of the character fields the API doesn't
// return directly: per-skill SP cost/rate, and the attribute/armor pools
// (Body/Armor Cap/Dex Armor/Agility/Arcane/Nature/Spirit/Stamina/
// Thaumaturgy), which are computed from specific skills' ranks rather than
// being columns on the character resource. This is the single source of
// truth for that math — shared by the interactive editor (CharacterSkills)
// and the printable/preview character sheet (CharacterSheetPrintPage), so
// both show the same numbers.
import { calculateSpCost } from '../../utils/calculateSpCost';

// Normalized names of the two "slotted" groups. Within each group the character
// picks (implicitly) a Primary / Secondary / Tertiary slot; the slot is assigned
// automatically so the cheapest tier always goes to the most-purchased skill.
// NOTE: names must match the *normalized* (trimmed, lower-cased) skill name.
// "Rune Carving" normalizes to "rune carving" (with a space) — not "runecarving".
const GATHERING_SKILL_NAMES = ["mining", "foraging", "logging"];
const PRODUCTION_SKILL_NAMES = ["smithing", "alchemy", "rune carving"];

const normalizeName = (name) => (name || '').trim().toLowerCase();

// Pure cost engine: given the current skill list + the catalog of available
// skills + the character (for primary/secondary skillset), return a map of
// skillId -> SP cost. This is the single source of truth for every SP number in
// the UI. It does NOT read component state, so it can be safely called with the
// "next" character that is still being computed inside a state updater.
export function computeSkillCosts(skills, availableSkills, character) {
    const result = {};
    const baseById = (id) => availableSkills.find(a => a.skillId === id);
    const nameOf = (s) => normalizeName(s.name || (baseById(s.skillId) || {}).name);

    // Slotted groups: sort members by ranks (desc), then skillId (asc) for a
    // stable tie-break, and assign Primary/Secondary/Tertiary tiers by position.
    const assignGroup = (groupNames) => {
        const members = skills
            .filter(s => groupNames.includes(nameOf(s)))
            .slice()
            .sort((a, b) => (Number(b.ranks) || 0) - (Number(a.ranks) || 0) || (a.skillId - b.skillId));

        members.forEach((s, idx) => {
            const base = baseById(s.skillId);
            if (!base) { result[s.skillId] = 0; return; }
            let baseCost, incrementCost;
            if (idx === 0) { baseCost = base.primaryBaseCost; incrementCost = base.primaryIncrementCost; }
            else if (idx === 1) { baseCost = base.secondaryBaseCost; incrementCost = base.secondaryIncrementCost; }
            else if (idx === 2) { baseCost = base.tertiaryBaseCost; incrementCost = base.tertiaryIncrementCost; }
            else { baseCost = 0; incrementCost = 0; }
            result[s.skillId] = calculateSpCost(baseCost, incrementCost, Number(s.ranks) || 0);
        });
    };
    assignGroup(GATHERING_SKILL_NAMES);
    assignGroup(PRODUCTION_SKILL_NAMES);

    // Everything else: single-purchase skills use the primary tier; tiered skills
    // pick their tier from the character's primary/secondary skillset.
    skills.forEach(s => {
        if (Object.prototype.hasOwnProperty.call(result, s.skillId)) return; // already costed (slotted)
        const base = baseById(s.skillId);
        if (!base) { result[s.skillId] = 0; return; }
        let baseCost, incrementCost;
        if (base.secondaryBaseCost == null) {
            baseCost = base.primaryBaseCost; incrementCost = base.primaryIncrementCost;
        } else if (Number(base.skillset) === Number(character?.skillsetPrimary)) {
            baseCost = base.primaryBaseCost; incrementCost = base.primaryIncrementCost;
        } else if (Number(base.skillset) === Number(character?.skillsetSecondary)) {
            baseCost = base.secondaryBaseCost; incrementCost = base.secondaryIncrementCost;
        } else {
            baseCost = base.tertiaryBaseCost; incrementCost = base.tertiaryIncrementCost;
        }
        result[s.skillId] = calculateSpCost(baseCost, incrementCost, Number(s.ranks) || 0);
    });

    return result;
}

// Which base+increment costs apply to *this* skill for the current character?
// Used only for the per-skill annotations (baseCostUsed/incrementCostUsed).
export function getSkillRate(skill, availableSkills, character) {
    const baseSkill = availableSkills.find(as => as.skillId === skill.skillId);
    if (!baseSkill) return { baseCost: 0, incrementCost: 0 };

    const normalizedName = normalizeName(skill.name || baseSkill.name);
    const characterSkills = character?.skills || [];

    const isGather = GATHERING_SKILL_NAMES.includes(normalizedName);
    const isProduce = PRODUCTION_SKILL_NAMES.includes(normalizedName);

    // For slotted groups, assign Primary/Secondary/Tertiary by highest ranks
    if (isGather || isProduce) {
        const groupNames = isGather ? GATHERING_SKILL_NAMES : PRODUCTION_SKILL_NAMES;

        const filteredAndSorted = characterSkills
            .filter(s => groupNames.includes(normalizeName(s.name || (availableSkills.find(as => as.skillId === s.skillId) || {}).name)))
            .sort((a, b) => (b.ranks || 0) - (a.ranks || 0) || (a.skillId - b.skillId));

        const idx = filteredAndSorted.findIndex(s => s.skillId === skill.skillId);
        if (idx === 0) return {
            baseCost: baseSkill.primaryBaseCost ?? 0,
            incrementCost: baseSkill.primaryIncrementCost ?? 0
        };
        if (idx === 1) return {
            baseCost: baseSkill.secondaryBaseCost ?? 0,
            incrementCost: baseSkill.secondaryIncrementCost ?? 0
        };
        if (idx === 2) return {
            baseCost: baseSkill.tertiaryBaseCost ?? 0,
            incrementCost: baseSkill.tertiaryIncrementCost ?? 0
        };
        // not in top three → no cost
        return { baseCost: 0, incrementCost: 0 };
    }

    // Single-purchase skills: use primary tier
    if (baseSkill.secondaryBaseCost == null) {
        return {
            baseCost: baseSkill.primaryBaseCost ?? 0,
            incrementCost: baseSkill.primaryIncrementCost ?? 0
        };
    }

    // Otherwise pick tier by char's primary/secondary
    if (Number(baseSkill.skillset) === Number(character?.skillsetPrimary)) {
        return {
            baseCost: baseSkill.primaryBaseCost ?? 0,
            incrementCost: baseSkill.primaryIncrementCost ?? 0
        };
    } else if (Number(baseSkill.skillset) === Number(character?.skillsetSecondary)) {
        return {
            baseCost: baseSkill.secondaryBaseCost ?? 0,
            incrementCost: baseSkill.secondaryIncrementCost ?? 0
        };
    } else {
        return {
            baseCost: baseSkill.tertiaryBaseCost ?? 0,
            incrementCost: baseSkill.tertiaryIncrementCost ?? 0
        };
    }
}

export const calculateBodyPoints = (skills) => {
    const toughnessSkill = skills.find(s =>
        Number(s.skillId) === 31 ||
        (s.name && s.name.trim().toLowerCase() === "toughness")
    );
    return toughnessSkill ? 10 + Number(toughnessSkill.ranks) * 5 : 10;
};

export const calculateDexArmor = (skills) => {
    const skill = skills.find(s =>
        Number(s.skillId) === 2 ||
        (s.name && s.name.trim().toLowerCase() === "dexterity armor")
    );
    return skill ? Number(skill.ranks) * 5 : 0;
};

export const calculateMaxArmor = (skills) => {
    const skill = skills.find(s =>
        Number(s.skillId) === 28 ||
        (s.name && s.name.trim().toLowerCase() === "armor training")
    );
    return skill ? Number(skill.ranks) * 5 : 0;
};

export const updatePoolPoints = (skills, poolName, availableSkills) => {
    const baseSkill = availableSkills.find(s => s.name && s.name.trim().toLowerCase() === poolName.toLowerCase());
    if (!baseSkill || !baseSkill.skillId) return 0;
    const poolSkill = skills.find(s => s.skillId === baseSkill.skillId);
    return poolSkill ? Number(poolSkill.ranks) * 10 : 0;
};

export function shallowEqualSkills(a = [], b = []) {
    if (a === b) return true;
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
        const x = a[i], y = b[i];
        if (x.skillId !== y.skillId) return false;
        if (Number(x.ranks || 0) !== Number(y.ranks || 0)) return false;
        if (Number(x.cost || 0) !== Number(y.cost || 0)) return false;
        if (Number(x.baseCostUsed || 0) !== Number(y.baseCostUsed || 0)) return false;
        if (Number(x.incrementCostUsed || 0) !== Number(y.incrementCostUsed || 0)) return false;
    }
    return true;
}

// Annotates each skill with {cost, baseCostUsed, incrementCostUsed} and adds
// the derived attribute/armor pools + skillPointsSpent/skillPointsUnspent
// onto the character. skillCatalog is required (unlike the editor's local
// wrapper, this has no component state to default it from).
export function updateCalculatedFields(character, skillCatalog) {
    const skills = character.skills || [];

    // Cost every skill from the *passed-in* character (never component state),
    // so buying/selling computes against the new ranks, not the previous render.
    const costs = computeSkillCosts(skills, skillCatalog, character);

    // Add computed props per skill for printing
    const skillsWithComputed = skills.map(s => {
        const { baseCost, incrementCost } = getSkillRate(s, skillCatalog, character);
        const cost = costs[s.skillId] || 0;
        return { ...s, cost, baseCostUsed: baseCost, incrementCostUsed: incrementCost };
    });

    // Reuse the previous array if annotations didn't actually change
    const skillsUnchanged = shallowEqualSkills(skills, skillsWithComputed);
    const finalSkills = skillsUnchanged ? skills : skillsWithComputed;

    const spent = skillsWithComputed.reduce((acc, skill) => acc + (skill.cost || 0), 0);

    const computedValues = {
        bodyPoints: calculateBodyPoints(skillsWithComputed),
        maxArmor: calculateMaxArmor(skillsWithComputed) || 0,
        dexArmor: calculateDexArmor(skillsWithComputed) || 0,
        agilityPoints: updatePoolPoints(skillsWithComputed, "Agility Points", skillCatalog),
        arcanePoints: updatePoolPoints(skillsWithComputed, "Attunement: Arcane", skillCatalog),
        naturePoints: updatePoolPoints(skillsWithComputed, "Attunement: Nature", skillCatalog),
        spiritPoints: updatePoolPoints(skillsWithComputed, "Attunement: Spirit", skillCatalog),
        staminaPoints: updatePoolPoints(skillsWithComputed, "Stamina Points", skillCatalog),
        thaumaturgyPoints: updatePoolPoints(skillsWithComputed, "Attunement: Thaumaturgy", skillCatalog),
        skillPointsSpent: spent,
        skillPointsUnspent: (character.skillPointsEffective || character.skillPointsTotal || 0) - spent
    };

    return { ...character, ...computedValues, skills: finalSkills };
}
